// Pointer-based drag-to-reorder for Home's edit-layout mode. No
// library: pointer events cover mouse + touch + pen with one code
// path. The drag starts from a dedicated handle (touch-action: none
// on the handle only, so the rest of the card still scrolls the page
// on phones), shows a compact ghost pill that follows the pointer,
// and live-reorders the list as the pointer crosses other cards.
//
// Hit-testing is elementFromPoint → closest('[data-drag-key]') inside
// the container, so it works for vertical stacks and wrapped grids
// alike, and CSS `order`-based visual arrangements (the section list)
// hit-test correctly because elementFromPoint sees visual layout.

// Move dragKey to overKey's slot. Pure — exported for tests.
export function reorderPreview(arr, dragKey, overKey) {
  const i = arr.indexOf(dragKey);
  const j = arr.indexOf(overKey);
  if (i < 0 || j < 0 || i === j) return arr;
  const next = arr.slice();
  next.splice(i, 1);
  next.splice(j, 0, dragKey);
  return next;
}

const EDGE_PX = 90;
const SCROLL_SPEED = 14;

export function startReorderDrag({ event, container, key, label, onPreview, onCommit, onCancel }) {
  if (!container) return;
  // Primary button / touch only.
  if (event.button != null && event.button !== 0) return;
  event.preventDefault();
  event.stopPropagation();

  const ghost = document.createElement('div');
  ghost.className = 'hv-drag-ghost font-heading';
  ghost.textContent = label;
  document.body.appendChild(ghost);
  const place = (x, y) => {
    ghost.style.left = `${x + 14}px`;
    ghost.style.top = `${y + 14}px`;
  };
  place(event.clientX, event.clientY);

  // Mark the element being moved so the user always sees WHICH card is
  // in flight — the ghost shows where the pointer is, the source
  // highlight shows what will land there. Keyed React lists keep the
  // DOM node across preview reorders, so the manual class survives.
  const src = container.querySelector(`[data-drag-key="${CSS.escape(key)}"]`);
  src?.classList.add('hv-drag-source');

  // Auto-scroll when the pointer parks near the viewport edges —
  // without this a phone user can't drag a card further than one
  // screenful.
  let lastY = event.clientY;
  let raf = 0;
  const tick = () => {
    if (lastY < EDGE_PX) window.scrollBy(0, -SCROLL_SPEED);
    else if (lastY > window.innerHeight - EDGE_PX) window.scrollBy(0, SCROLL_SPEED);
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  const onMove = (e) => {
    place(e.clientX, e.clientY);
    lastY = e.clientY;
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const over = el?.closest('[data-drag-key]');
    if (!over || !container.contains(over)) return;
    const overKey = over.getAttribute('data-drag-key');
    if (overKey && overKey !== key) onPreview(key, overKey);
  };

  const cleanup = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onPointerCancel);
    cancelAnimationFrame(raf);
    ghost.remove();
    src?.classList.remove('hv-drag-source');
  };
  const onUp = () => {
    cleanup();
    onCommit();
  };
  const onPointerCancel = () => {
    cleanup();
    onCancel();
  };

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onPointerCancel);
}
